import type { DirectionCard, TypeAnswers } from "../domain.js";
import type { TypePath } from "./flow.js";
import { HARMONY_ENGINE_VERSION } from "./harmony-engine.js";
import { TYPE_ENGINE_VERSION } from "./type-engine.js";

export type TypeSetProjectFile = {
  schemaVersion: "1.0.0";
  product: "Type Set";
  engineVersions: { type: string; direction: string };
  projectId: string;
  revision: number;
  createdAt: string;
  updatedAt: string;
  answers: { type: TypeAnswers; typePath?: TypePath };
  direction: DirectionCard;
  privacy: {
    containsPreviewCopy: boolean;
    localOnly: true;
  };
};

const FORBIDDEN_KEYS = new Set(["__proto__", "prototype", "constructor"]);
const PRIVATE_PAYLOAD_KEYS = new Set(["originalFilename", "imageBytes", "imagePixels", "rgba", "dataUrl", "objectUrl"]);
const TOP_LEVEL_KEYS = new Set(["schemaVersion", "product", "engineVersions", "projectId", "revision", "createdAt", "updatedAt", "answers", "direction", "privacy"]);

const isRecord = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === "object" && !Array.isArray(value);
const assertAllowedKeys = (record: Record<string, unknown>, allowed: ReadonlySet<string>, label: string): void => {
  const extra = Object.keys(record).find((key) => !allowed.has(key));
  if (extra) throw new Error(`${label} contains unknown key: ${extra}`);
};
function assertDateTime(value: unknown, label: string): asserts value is string {
  if (typeof value !== "string" || value.trim() === "" || Number.isNaN(Date.parse(value))) throw new Error(`${label} must be an ISO date-time string.`);
}

const inspectTree = (value: unknown, depth = 0): void => {
  if (depth > 24) throw new Error("Project file exceeds maximum nesting depth.");
  if (value === null || typeof value !== "object") return;
  if (Array.isArray(value)) {
    if (value.length > 100_000) throw new Error("Project file contains an oversized array.");
    value.forEach((item) => inspectTree(item, depth + 1));
    return;
  }
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    if (FORBIDDEN_KEYS.has(key)) throw new Error(`Project file contains forbidden key: ${key}`);
    if (PRIVATE_PAYLOAD_KEYS.has(key)) throw new Error(`Project file contains forbidden private payload key: ${key}`);
    inspectTree(child, depth + 1);
  }
};

const hasPreviewCopy = (direction: DirectionCard): boolean =>
  Object.values(direction.previewCopy).some((value) => typeof value === "string" && value.trim().length > 0);

export const createProjectFile = (input: {
  direction: DirectionCard;
  typeAnswers: TypeAnswers;
  typePath: TypePath;
  createdAt: string;
  updatedAt?: string;
  revision?: number;
}): TypeSetProjectFile => {
  if (!input.direction.projectId.trim() || input.direction.projectId.length > 120) throw new Error("projectId must contain 1–120 characters.");
  if (!Number.isInteger(input.revision ?? 1) || (input.revision ?? 1) < 1) throw new Error("revision must be a positive integer.");
  assertDateTime(input.createdAt, "createdAt");
  assertDateTime(input.updatedAt ?? input.createdAt, "updatedAt");
  if (input.direction.product !== "Type Set") throw new Error("A Type Set project needs a Type Set direction.");
  const project: TypeSetProjectFile = {
    schemaVersion: "1.0.0",
    product: "Type Set",
    engineVersions: { type: TYPE_ENGINE_VERSION, direction: HARMONY_ENGINE_VERSION },
    projectId: input.direction.projectId,
    revision: input.revision ?? 1,
    createdAt: input.createdAt,
    updatedAt: input.updatedAt ?? input.createdAt,
    answers: { type: input.typeAnswers, typePath: input.typePath },
    direction: input.direction,
    privacy: { containsPreviewCopy: hasPreviewCopy(input.direction), localOnly: true },
  };
  inspectTree(project);
  return project;
};

export const serializeProjectFile = (project: TypeSetProjectFile): string => {
  inspectTree(project);
  return `${JSON.stringify(project, null, 2)}\n`;
};

export const parseProjectFile = (json: string): TypeSetProjectFile => {
  if (new TextEncoder().encode(json).byteLength > 2_000_000) throw new Error("Project file exceeds 2 MB import limit.");
  const parsed: unknown = JSON.parse(json);
  inspectTree(parsed);
  if (!isRecord(parsed)) throw new Error("Project file root must be an object.");
  assertAllowedKeys(parsed, TOP_LEVEL_KEYS, "Project file root");
  if (parsed.schemaVersion !== "1.0.0" || parsed.product !== "Type Set") throw new Error("This is not a supported Type Set project file.");
  if (typeof parsed.projectId !== "string" || parsed.projectId.trim() === "" || parsed.projectId.length > 120) throw new Error("Project file needs a 1–120 character projectId.");
  if (!Number.isInteger(parsed.revision) || (parsed.revision as number) < 1) throw new Error("Project revision must be a positive integer.");
  assertDateTime(parsed.createdAt, "createdAt");
  assertDateTime(parsed.updatedAt, "updatedAt");
  if (!isRecord(parsed.engineVersions)) throw new Error("Project engineVersions must be an object.");
  assertAllowedKeys(parsed.engineVersions, new Set(["type", "direction"]), "engineVersions");
  if (typeof parsed.engineVersions.type !== "string" || typeof parsed.engineVersions.direction !== "string") throw new Error("Project engine versions are incomplete.");
  if (!isRecord(parsed.answers) || !isRecord(parsed.answers.type)) throw new Error("Project answers need a type object.");
  assertAllowedKeys(parsed.answers, new Set(["type", "typePath"]), "answers");
  if (parsed.answers.typePath !== undefined && parsed.answers.typePath !== "quick" && parsed.answers.typePath !== "deep") {
    throw new Error("Project typePath must be quick or deep.");
  }
  if (!isRecord(parsed.direction) || parsed.direction.schemaVersion !== "1.0.0" || parsed.direction.projectId !== parsed.projectId) throw new Error("Project direction is missing, unsupported, or belongs to another projectId.");
  if (parsed.direction.product !== "Type Set" || !isRecord(parsed.direction.selected)) throw new Error("Project direction is not a Type Set direction.");
  if (!isRecord(parsed.direction.previewCopy)) throw new Error("Project direction needs previewCopy object.");
  const privacy = isRecord(parsed.privacy) ? parsed.privacy : undefined;
  if (!privacy || privacy.localOnly !== true || typeof privacy.containsPreviewCopy !== "boolean") throw new Error("Project privacy contract is missing or unsafe.");
  assertAllowedKeys(privacy, new Set(["containsPreviewCopy", "localOnly"]), "privacy");
  if (privacy.containsPreviewCopy !== hasPreviewCopy(parsed.direction as unknown as DirectionCard)) throw new Error("privacy.containsPreviewCopy does not match direction preview copy.");
  return parsed as unknown as TypeSetProjectFile;
};
